import React, { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../utils/roles';
import DataTable from '../components/DataTable';
import EditCensorModal from '../components/EditCensorModal';
import CreateCensorModal from '../components/CreateCensorModal';
import './StaffPage.css';

const StaffPage = () => {
  const { user } = useAuth();
  const [staffList, setStaffList] = useState([]);
  const [centers, setCenters] = useState([]); // Liste til opslag af navne
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');

  const [editingPerson, setEditingPerson] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState({ relevant: false, approved: false, terminNavn: '' });

  const [statusFilters, setStatusFilters] = useState({
    'Tilgængelig': true,
    'Syg': true,
    'Orlov': true,
    'Barsel': true,
    'Ferie': true,
    'Pause': true,
    'Inaktiv': true
  });

  const [examFilters, setExamFilters] = useState({
    PD1: true,
    PD2: true,
    PD3: true,
    SP: true, // Studieprøven
    VP: true // Vidensprøver (hvis du har det i DB, ellers kan det udelades)
  });

  useEffect(() => {
    if (user) {
      fetchStaff();
      if (user.role === ROLES.ADMIN) fetchCenters();
      
      if (user.role === ROLES.SC_ADMIN) fetchApprovalStatus();
    }
  }, [user]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/users?type=censor');
      setStaffList(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Fejl', err);
      setLoading(false);
    }
  };

  // Fetch funktion
  const fetchApprovalStatus = async () => {
    try {
      const res = await apiClient.get('/api/censorer/approval-status');
      setApprovalStatus(res.data);
    } catch(err) { console.error(err); }
  };

  // Handle Godkendelse
  const handleApprove = async () => {
    if (!window.confirm(`Er du sikker på, at alle censoroplysninger er korrekte for ${approvalStatus.terminNavn}?`)) return;
    
    try {
      // Vi genbruger endpointet fra dashboardet, da det gør præcis det samme
      await apiClient.post('/api/dashboard/approve-censors');
      
      setApprovalStatus(prev => ({ ...prev, approved: true }));
      alert('Oplysninger godkendt!');
    } catch(err) { alert('Fejl ved godkendelse'); }
  };

  // Hent centre (Reference data)
  const fetchCenters = async () => {
    try {
      const res = await apiClient.get('/api/sprogcentre');
      setCenters(res.data);
    } catch (err) { console.error('Kunne ikke hente centre', err); }
  };

  // Håndter klik på checkboxes
  const toggleExamFilter = (key) => {
    setExamFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Toggle funktion for status
  const toggleStatusFilter = (key) => setStatusFilters(prev => ({ ...prev, [key]: !prev[key] }));

  // --- LOGIK: OPRET (FIXED) ---
  const handleCreate = async (newData) => {
    try {
      // 1. Send data til API
      const res = await apiClient.post('/api/users', { ...newData, type: 'censor' });
      const createdPerson = res.data;

      // 2. FIND NAVNET PÅ SPROGCENTERET (Frontend Patch)
      // Vi har ID'et fra den oprettede person, nu finder vi navnet i vores centers liste
      const matchedCenter = centers.find(c => c.id === createdPerson.sprogcenter_id);

      // 3. Sammensæt det "komplette" objekt til tabellen
      const completePerson = {
        ...createdPerson,
        // Hvis vi fandt centeret, brug navnet. Ellers fallback (fx hvis SC_ADMIN)
        sprogcenter_navn: matchedCenter ? matchedCenter.navn : 'Mit Sprogcenter',
        // Sikr at status er sat (backend bør returnere det, men vi garderer os)
        arbejds_status: createdPerson.arbejds_status || newData.arbejds_status
      };

      // 4. Opdater listen med det berigede objekt
      setStaffList(prev => [...prev, completePerson]);
      await fetchStaff();

      setShowCreateModal(false);
      alert('Person oprettet succesfuldt!');
    } catch (err) {
      console.error(err);
      alert('Fejl ved oprettelse.');
    }
  };

  // --- LOGIK: REDIGER ---
  const handleUpdate = async (updatedData) => {
    try {
      await apiClient.put(`/api/users/${updatedData.id}?type=censor`, updatedData);
      setStaffList(prev => prev.map(p => p.id === updatedData.id ? { ...p, ...updatedData } : p));
      setEditingPerson(null);
    } catch (err) { alert('Fejl ved opdatering.'); }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/api/users/${id}`);
      setStaffList(prev => prev.filter(p => p.id !== id));
      setEditingPerson(null);
    } catch (err) { alert('Fejl ved sletning.'); }
  };

  const isCensor = (person) => {
    return (
      person.beskikket_pd1 || 
      person.beskikket_pd2 || 
      person.beskikket_pd3 || 
      person.beskikket_sp || 
      person.beskikket_indfodsret || 
      person.beskikket_medborgerskab
    );
  };

  // Filtreringslogik: Vi filtrerer på flere lag - Søgning, Rolle, Prøve, og Status. Alle skal passe for at en person vises.
  const filteredList = staffList.filter(person => {
    // 1. Søgning
    const fullName = `${person.fornavn} ${person.efternavn}`.toLowerCase();
    const search = searchTerm.toLowerCase();
    const matchesSearch = fullName.includes(search) || person.email?.toLowerCase().includes(search);

    // 2. Type Filtrering (Censor / Underviser)
    let matchesType = true;
    if (filterType === 'CENSOR') matchesType = isCensor(person);
    else if (filterType === 'TEACHER') matchesType = !isCensor(person);

    // 3. Prøve Filtrering (Checkboxes i toppen)
    let matchesExam = true;
    if (isCensor(person) && filterType !== 'TEACHER') {
        const activeFilters = Object.keys(examFilters).filter(key => examFilters[key]);
        
        if (activeFilters.length === 0) {
            matchesExam = false; 
        } else {
            // Oversæt personens DB-felter til vores filter-nøgler
            const personSkills = {
                PD1: person.beskikket_pd1,
                PD2: person.beskikket_pd2,
                PD3: person.beskikket_pd3,
                SP: person.beskikket_sp,
                VP: person.beskikket_indfodsret || person.beskikket_medborgerskab
            };
            
            // Hvis personen har bare én af de færdigheder vi har sat kryds i filteret for, så vis dem
            matchesExam = activeFilters.some(filterKey => personSkills[filterKey] === true);
        }
    }

    // 4. Status Filtrering
    let matchesStatus = true;
    const status = person.arbejds_status || 'Tilgængelig';
    if (statusFilters[status] !== undefined) matchesStatus = statusFilters[status];

    return matchesSearch && matchesType && matchesExam && matchesStatus;
  });

  const getStatusColor = (status) => {
    if (!status) return 'status-gray';
    const s = status.toString().toLowerCase();
    if (s.includes('tilgængelig')) return 'status-green';
    if (s.includes('syg') || s.includes('inaktiv') || s.includes('orlov') || s.includes('barsel') || s.includes('ferie')) return 'status-red';
    if (s.includes('pause')) return 'status-yellow';
    return 'status-gray';
  };

  const columns = [
    {
      header: 'Navn',
      width: '22%',
      accessor: 'fornavn',
      // Vi bruger 'render' fordi vi vil vise CPR eller fed tekst
      render: (row) => (
        <div>
          <strong>{row.fornavn} {row.efternavn}</strong>
        </div>
      )
    },
    {
      header: 'Kontakt',
      width: '20%',
      accessor: 'email',
      render: (row) => (
        <div>
          <div>{row.email}</div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>{row.telefon}</div>
        </div>
      )
    },
    {
      header: 'Adresse',
      width: '20%',
      accessor: 'adresse', // Sorterer på vejnavn
      render: (row) => (
        <div>
          <div>{row.adresse || '-'}</div>
          {row.postnummer && (
            <div style={{ fontSize: '0.8rem', color: '#666' }}>
              {row.postnummer} {row.forstad}
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Beskikkelser',
      width: '20%',
      render: (row) => {
        // Bruger vores nye helper funktion
        if (!isCensor(row)) {
            return <span className="role-badge role-teacher">Underviser</span>;
        }

        // Hvis de er censor, vis alle badges i farver
        const badges =[
            { label: 'PD1', active: row.beskikket_pd1 },
            { label: 'PD2', active: row.beskikket_pd2 },
            { label: 'PD3', active: row.beskikket_pd3 },
            { label: 'SP',  active: row.beskikket_sp },
            { label: 'VP',  active: row.beskikket_indfodsret || row.beskikket_medborgerskab }
        ];

        return (
            <div className="badge-row">
                {badges.map(b => (
                    <span key={b.label} className={`exam-badge ${b.active ? 'active' : 'inactive'}`}>
                        {b.label}
                    </span>
                ))}
            </div>
        );
      }
    },
    {
      header: 'Status',
      width: '10%',
      accessor: 'arbejds_status',
      render: (row) => (
        <span className={`status-badge ${getStatusColor(row.arbejds_status)}`}>
          {row.arbejds_status || 'Ukendt'}
        </span>
      )
    },
    {
      header: '',
      width: '8%',
      align: 'right', // Juster til højre
      render: (row) => (
        <button onClick={() => setEditingPerson(row)} className="edit-btn">
          Rediger
        </button>
      )
    }
  ];

  return (
    <div>

      <div className="table-card">
        
        <div className="card-header-content">
          
          {/* --- RÆKKE 1: ROLLER + GODKEND --- */}
          <div style={{
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexWrap: 'wrap', 
            gap: '20px', 
            paddingBottom: '15px',
          }}>
            
            {/* 1. Rolle Filter */}
            <div className="filter-buttons">
              <button className={`filter-btn ${filterType === 'ALL' ? 'active' : ''}`} onClick={() => setFilterType('ALL')}>Alle</button>
              <button className={`filter-btn ${filterType === 'CENSOR' ? 'active' : ''}`} onClick={() => setFilterType('CENSOR')}>Censorer</button>
              <button className={`filter-btn ${filterType === 'TEACHER' ? 'active' : ''}`} onClick={() => setFilterType('TEACHER')}>Undervisere</button>
            </div>

            {/* 2. Godkend Knap (Ligger til venstre, lige efter rollerne) */}
            {approvalStatus.relevant && (
              
              <div style={{
            display: 'flex',  
            flexWrap: 'wrap', 
            gap: '17px',
          }}>
                <button
                  onClick={handleApprove}
                  disabled={approvalStatus.approved}
                  className={approvalStatus.approved ? 'btn-approved' : 'btn-approve-action'}
                  title={approvalStatus.approved ? `Godkendt for ${approvalStatus.terminNavn}` : `Godkend for ${approvalStatus.terminNavn}`}
                  style={{height: '40px', display: 'flex', alignItems: 'center'}} // Matcher højden på filter-knapperne
                >
                  {approvalStatus.approved ? '✅ Oplysninger godkendt' : 'Godkend oplysninger'}
                </button>

                <button className="action-btn" onClick={() => setShowCreateModal(true)}>Opret ny</button>
              </div>
            )}

            

          </div>


          {/* --- RÆKKE 2: PRØVER + STATUS (Venstre) --- SØG/NY (Højre) --- */}
          <div style={{
            display: 'flex', 
            justifyContent: 'space-between', // Skubber venstre og højre gruppe fra hinanden
            alignItems: 'center', 
            flexWrap: 'wrap', 
            gap: '15px'
          }}>
            
            {/* VENSTRE GRUPPE: PRØVER + STATUS */}
            <div style={{display: 'flex', gap: '15px', flexWrap: 'wrap'}}>
                
                {/* 1. Prøve Filter (Kun hvis relevant) */}
                {filterType !== 'TEACHER' && (
                    <div className="filter-buttons">
                        {Object.keys(examFilters).map(key => (
                            <label key={key} className={`filter-btn ${examFilters[key] ? 'active' : ''}`} style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}}>
                                <input type="checkbox" checked={examFilters[key]} onChange={() => toggleExamFilter(key)} style={{display: 'none'}} />
                                {key}
                            </label>
                        ))}
                    </div>
                )}

                {/* 2. Status Filter */}
                <div className="filter-buttons">
                    {Object.keys(statusFilters).map(key => (
                        <label key={key} className={`filter-btn ${statusFilters[key] ? 'active' : ''}`} style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}}>
                            <input type="checkbox" checked={statusFilters[key]} onChange={() => toggleStatusFilter(key)} style={{display: 'none'}} />
                            {key}
                        </label>
                    ))}
                </div>
            </div>

            {/* HØJRE GRUPPE: SØG + OPRET */}
            <div style={{display:'flex', gap:'10px', alignItems: 'center'}}>
              <input type="text" className="search-input" placeholder="Søg..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>

          </div>
        </div>

        <DataTable 
          columns={columns}
          data={filteredList}
          loading={loading}
          noDataMessage="Ingen personer fundet med de valgte filtre."
          initialSortKey="fornavn"
        />
        
      </div>

      {/* Modaler ... */}
      {editingPerson && (
        <EditCensorModal
          censor={editingPerson}
          onClose={() => setEditingPerson(null)}
          onSave={handleUpdate}
          onDelete={handleDelete}
          user={user}
        />
      )}

      {showCreateModal && (
        <CreateCensorModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
          user={user}
          centers={centers}
        />
      )}
    </div>
  );
};

export default StaffPage;
